// ============================================================
// Commandery Secure Access — member-facing wiring + UI state contract.
//
// The clean surface the Secure Portal UI plugs into. Square Code proxies the
// Olivium Secure Share engine (redeem + verify-session); Olivium stays the
// access authority and the trust identity ("Protected by Olivium"). This module
// is transport-only — it NEVER holds or exposes a session token, binding, hash,
// raw cookie, or JWT (the browser-bound session lives in an httpOnly cookie the
// server mints), and it NEVER puts the one-time access code in a URL, in
// storage, in logs, or in a receipt.
//
// Implements docs/commandery-access-state-contract.json (states + forbidden
// fields). See docs/commandery/COMMANDERY_LANDING_VERIFY_WIRING_RECEIPT.md.
// ============================================================

import { api } from '../../api/client'
import {
  createAccessInviteDryRun, createAccessInviteLive,
  type CreateAccessInput, type CreateAccessResult,
} from '../Projects/accessControl'

// ── UI state contract (implements commandery-access-state-contract.json) ──
//
// Every state is member-SAFE: a title + message with no secret, no env name, no
// raw provider/debug detail. `reason` echoes the state code for safe telemetry.

export interface CommanderyStateInfo {
  code: CommanderyAccessState
  title: string
  message: string
  safe: true
  reason: CommanderyAccessState
}

export const COMMANDERY_ACCESS_STATES = {
  idle: { title: 'Commandery Secure Access', message: 'Enter your invite reference and one-time access code to continue.' },
  submitting: { title: 'Submitting secure access', message: 'Sending your access code securely…' },
  verifying: { title: 'Verifying secure access …', message: 'Confirming this code has not been used, expired, or revoked. This takes just a moment.' },
  verified: { title: 'Secure access verified', message: 'Your secure access is verified.' },
  denied: { title: 'Access not granted', message: 'This access code can’t be used. Check the code and try again.' },
  expired: { title: 'Access expired', message: 'This access has expired. Ask your Commandery contact for a new invite.' },
  revoked: { title: 'Access revoked', message: 'This access has been revoked. Ask your Commandery contact for a new invite.' },
  already_used: { title: 'Access code already used', message: 'This access code has already been used.' },
  no_session: { title: 'No secure session found', message: 'No secure session was found. Please redeem your access code again.' },
  not_live: { title: 'Secure portal not live', message: 'Secure access is not open yet. Please try again later.' },
  error: { title: 'Secure access unavailable', message: 'Secure access is temporarily unavailable. Please try again.' },
  account_required: { title: 'Account required', message: 'A Commandery account is required to continue.' },
  account_link_required: { title: 'Link account', message: 'Link your Commandery account to continue.' },
  account_bootstrap_not_implemented: { title: 'Account setup not available yet', message: 'Secure access is verified, but member account setup isn’t available yet.' },
} as const
export type CommanderyAccessState = keyof typeof COMMANDERY_ACCESS_STATES

// Fields that must NEVER appear in any URL, response, log, or receipt
// (mirrors the contract's forbidden_fields).
export const COMMANDERY_FORBIDDEN_FIELDS = [
  'access_code_in_url', 'service_role', 'code_hash', 'session_token',
  'binding_hash', 'raw_cookie', 'raw_jwt', 'provider_error', 'debug_details',
] as const

// The one function every state flows through — returns a fully-formed, safe UI
// state object. Accepts a known state code or an unknown value (→ `error`).
export function getCommanderyAccessState(input: unknown): CommanderyStateInfo {
  const code: CommanderyAccessState =
    typeof input === 'string' && input in COMMANDERY_ACCESS_STATES
      ? (input as CommanderyAccessState)
      : 'error'
  const { title, message } = COMMANDERY_ACCESS_STATES[code]
  return { code, title, message, safe: true, reason: code }
}

// ── URL rules ──
//
// Allowed:   /commandery/secure-portal/access?invite=<invite_id>
// Forbidden: any `code` in the URL. buildCommanderyRedeemUrl has NO code param,
// so an access code cannot be placed in a URL through this surface.

export const COMMANDERY_PORTAL_ACCESS_PATH = '/commandery/secure-portal/access'
export const COMMANDERY_PORTAL_SESSION_PATH = '/commandery/secure-portal/session'

export function buildCommanderyRedeemUrl(opts: { inviteId?: string | null; basePath?: string } = {}): string {
  const base = opts.basePath ?? COMMANDERY_PORTAL_ACCESS_PATH
  const invite = (opts.inviteId ?? '').trim()
  return invite ? `${base}?invite=${encodeURIComponent(invite)}` : base
}

// Prefill ONLY the invite reference from a query string. The `code` param is
// deliberately never read — an access code must be delivered out-of-band.
export function parseCommanderyInvitePrefill(search: string | undefined | null): string {
  if (!search) return ''
  try {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get('invite')?.trim() ?? ''
  } catch {
    return ''
  }
}

// ── Redeem + verify wiring ──

export interface RedeemResult {
  ok: boolean
  state: CommanderyAccessState
  stateInfo: CommanderyStateInfo
  redeemed: boolean
  inviteId: string | null
  expiresAt: string | null
}

export interface VerifyResult {
  ok: boolean
  state: CommanderyAccessState
  stateInfo: CommanderyStateInfo
  verified: boolean
  inviteId: string | null
  sessionStatus: string | null
  expiresAt: string | null
  accountBootstrapped: boolean
  accountState: CommanderyAccessState | null
}

function resolveState(raw: unknown, fallback: CommanderyAccessState): CommanderyAccessState {
  return typeof raw === 'string' && raw in COMMANDERY_ACCESS_STATES ? (raw as CommanderyAccessState) : fallback
}

// Redeem the one-time access code. The code is passed straight to the server
// proxy and never persisted, echoed, or logged here.
export async function redeemCommanderyAccess(input: { inviteId: string; code: string }): Promise<RedeemResult> {
  try {
    const r = await api.access.redeem({ invite_id: input.inviteId.trim(), code: input.code })
    const state = resolveState(r.state, r.ok ? 'verifying' : 'error')
    return {
      ok: Boolean(r.ok),
      state,
      stateInfo: getCommanderyAccessState(state),
      redeemed: r.redeemed === true,
      inviteId: typeof r.invite_id === 'string' ? r.invite_id : input.inviteId.trim() || null,
      expiresAt: typeof r.expires_at === 'string' ? r.expires_at : null,
    }
  } catch {
    // Proxies answer HTTP 200 for normal outcomes, so a throw is a transport
    // fault → generic error. Never surface the raw error.
    return {
      ok: false, state: 'error', stateInfo: getCommanderyAccessState('error'),
      redeemed: false, inviteId: input.inviteId.trim() || null, expiresAt: null,
    }
  }
}

// Verify the browser-bound session (httpOnly cookie). No secret leaves the
// server; this only reports verified / not.
export async function verifyCommanderySession(input: { inviteId?: string } = {}): Promise<VerifyResult> {
  try {
    const r = await api.access.verifySession({ invite_id: input.inviteId })
    const state = resolveState(r.state, r.ok ? 'verified' : 'no_session')
    return {
      ok: Boolean(r.ok),
      state,
      stateInfo: getCommanderyAccessState(state),
      verified: r.verified === true,
      inviteId: typeof r.invite_id === 'string' ? r.invite_id : input.inviteId ?? null,
      sessionStatus: r.session && typeof r.session.status === 'string' ? r.session.status : null,
      expiresAt: r.session && typeof r.session.expires_at === 'string' ? r.session.expires_at : null,
      accountBootstrapped: r.account_bootstrapped === true,
      accountState: resolveAccountState(r.account_state),
    }
  } catch (err) {
    const state = throwState(err)
    return {
      ok: false, state, stateInfo: getCommanderyAccessState(state), verified: false,
      inviteId: input.inviteId ?? null, sessionStatus: null, expiresAt: null,
      accountBootstrapped: false, accountState: null,
    }
  }
}

function resolveAccountState(raw: unknown): CommanderyAccessState | null {
  return typeof raw === 'string' && raw in COMMANDERY_ACCESS_STATES ? (raw as CommanderyAccessState) : null
}

// A thrown ApiError is unexpected here (proxies answer 200 for normal outcomes),
// so a throw is always a transport fault → generic `error`.
function throwState(_err: unknown): CommanderyAccessState { return 'error' }

// ── Operator invite creation (unchanged authority; thin re-export) ──
//
// createCommanderyInvite() is the operator seam kept here so the Commandery
// surface has one import. It delegates to the existing gated adapter — dry-run
// by default; live only when explicitly requested (server enforces ownership +
// fail-closed live config). It returns the one-time access code ONCE, never a
// hash. This is operator-only and NOT part of the member portal.
export function createCommanderyInvite(
  input: CreateAccessInput,
  opts: { live?: boolean } = {},
): Promise<CreateAccessResult> {
  return opts.live ? createAccessInviteLive(input) : createAccessInviteDryRun(input)
}
